import axiosClient from '../../../api/axiosClient.js';
import { fetchPageData } from '../../../api/utils.js';

async function getCourses() {
  return fetchPageData(`/api/users/courses/`);
}

export const coursesQuery = () => {
  return {
    queryKey: ['courses'],
    queryFn: async () => getCourses(),
    refetchOnWindowFocus: false
  };
};

export async function createScenario({
  name,
  courses
}) {
  const { data } = await axiosClient.post(`api/games/scenarios/`, {
    name,
    is_published: true,
    courses
  });

  return data;
}

export async function scenarioAddCourse({scenarioId, courseId}) {
  const { data } = await axiosClient.put(`api/games/scenarios/${scenarioId}/add-course/`, { course_id: courseId });

  return data;
}

export async function scenarioBulkAddCourse({scenarioIds, courseId}) {
  const { data } = await axiosClient.post(`api/games/scenarios/bulk-add-course/`, {
    course_id: courseId,
    scenario_ids: scenarioIds
  });

  return data;
}

export async function createGame({
  name,
  description,
  startTime,
  endTime,
  gameType,
  scenarioId,
  courseId
}) {
  const gameTypeMapping = {
    'classification': 'ACC',
    'normal': 'NOR',
    'equation': 'BE',
    'principles': 'PR',
    'accounting cycle': 'AC',
  };
  
  const gameTypeValue = gameTypeMapping[gameType] || '';
  const { data } = await axiosClient.post(`api/games/games/create/`, {
    name,
    description,
    start_time: startTime,
    end_time: endTime,
    points: 100,
    is_active: true,
    game_type: gameTypeValue,
    scenario: scenarioId,
    course: courseId
  });

  return data;
}

async function getCourseSetup() {
  const response = await axiosClient.get(`/api/games/scenario-groups/course-setup/`);
  return response.data.scenarios;
}

export const coursesSetupQuery = () => {
  return {
    queryKey: ['courseSetup'],
    queryFn: async () => getCourseSetup(),
    refetchOnWindowFocus: false
  };
};

export async function createScenarioLevels({
  name,
  levelNumber,
  scenarios,
  description,
  design
}) {
  const { data } = await axiosClient.post(`api/games/scenario-levels/`, {
    name,
    falling_speed: 2,
    level: levelNumber,
    show_score: true,
    show_value: true,
    scenarios,
    description,
    design
  });

  return data;
}

async function getGamesItems() {
  return fetchPageData(`/api/games/items/`);
}

export const gamesItemsQuery = () => {
  return {
    queryKey: ['gamesItems'],
    queryFn: async () => getGamesItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function createItemQuestions({
  gameType,
  order,
  accountItemId,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification' 
    ? 'item-questions'
    : gameType === 'principles'
      ? 'principles-questions'
      : 'questions';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/`, {
    order,
    ...(gameType === 'equation' || gameType === 'principles' ? { transaction: accountItemId } : { account_item: accountItemId }),
    scenario_level: scenarioLevelId
  });

  return data;
}

export async function createItemOptions({
  gameType,
  order,
  accountItemId,
  scenarioLevelId
}) {
  const lastSlugPart = gameType === 'classification' ? 'item-options' : 'options';
  const { data } = await axiosClient.post(`api/${gameType}/${lastSlugPart}/`, {
    order,
    account_item: accountItemId,
    scenario_level: scenarioLevelId
  });

  return data;
}

// Atomic bulk-create endpoint: creates game + scenario + levels +
// nested questions and options in a single transaction.
export async function createGameFull({ gameType, payload }) {
  const { data } = await axiosClient.post(`/api/${gameType}/create-full/`, payload);
  return data;
}

async function getInstructorScenarioLevels({scenarioId}) {
  return scenarioId ? fetchPageData(`/api/games/instructor/scenario-levels/?scenarios=${scenarioId}`) : [];
}

export const scenarioLevelsQuery = ({scenarioId}) => {
  return {
    queryKey: [`instructorScenarioLevels`, scenarioId],
    queryFn: async () => getInstructorScenarioLevels({scenarioId}),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function getCourseMembers() {
  return fetchPageData(`/api/users/course-members/`);
}

export const courseMembersQuery = () => {
  return {
    queryKey: ['courseMembersReviewSession'],
    queryFn: async () => getCourseMembers(),
    refetchOnWindowFocus: false
  };
};

// Lightweight student-count endpoint — avoids paginating through the
// full /course-members/ roster (which can be 800+ rows) when only the
// count is needed (e.g. to size tournament rounds).
export async function getStudentCount() {
  const { data } = await axiosClient.get(`/api/users/course-members/counts/`);
  return (
    data?.students ??
    data?.student_count ??
    data?.count ??
    0
  );
}

export const studentCountQuery = () => {
  return {
    queryKey: ['studentCount'],
    queryFn: async () => getStudentCount(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  };
};

export async function getTournaments() {
  return fetchPageData(`/api/tournaments/tournaments/`);
}

export const tournamentsQuery = (pageId) => {
  return {
    queryKey: ['tournamentsInstructorOnboarding', pageId],
    queryFn: async () => getTournaments(),
    refetchOnWindowFocus: false
  };
};

export async function getTournamentsRounds({tournamentId}) {
  return fetchPageData(`/api/tournaments/rounds/?tournament_id=${tournamentId}`);
}

export const tournamentsRoundsQuery = ({tournamentId}) => {
  return {
    queryKey: ['tournamentsRounds', tournamentId],
    queryFn: async () => getTournamentsRounds({tournamentId}),
    refetchOnWindowFocus: false,
    enabled: !!tournamentId
  };
};

export async function getTournamentsTeams({tournamentId}) {
  return fetchPageData(`/api/tournaments/teams/?tournament_id=${tournamentId}`);
}

export const tournamentsTeamsQuery = ({tournamentId}) => {
  return {
    queryKey: ['tournamentsTeams', tournamentId],
    queryFn: async () => getTournamentsTeams({tournamentId}),
    refetchOnWindowFocus: false,
    enabled: !!tournamentId
  };
};

export async function createTournaments({
  name,
  courseId,
  roundCount
}) {
  const { data } = await axiosClient.post(`api/tournaments/tournaments/`, {
    name,
    course_id: courseId,
    round_count: roundCount
  });

  return data;
}

export async function updateTournamentsRounds({ roundId, gameId, name }) {
  const payload = {};
  if (gameId) payload.game_id = gameId;
  if (name) payload.name = name;

  const { data } = await axiosClient.patch(`api/tournaments/rounds/${roundId}/`, payload);
  return data;
}

export async function updateTournamentTeams({ teamId, name }) {
  const { data } = await axiosClient.patch(`api/tournaments/teams/${teamId}/`, {
    name
  });

  return data;
}

export async function assignTournamentStudents({ tournamentId }) {
  const { data } = await axiosClient.post(`api/tournaments/tournaments/assign-students/`, {
    tournament_id: tournamentId
  });

  return data;
}

// Atomic setup endpoint: rename rounds + teams and assign students
// in a single transaction.
export async function setupTournament({ tournamentId, rounds, teams, assignStudents }) {
  const payload = {};
  if (rounds) payload.rounds = rounds;
  if (teams) payload.teams = teams;
  if (typeof assignStudents === 'boolean') payload.assign_students = assignStudents;

  const { data } = await axiosClient.post(
    `api/tournaments/tournaments/${tournamentId}/setup/`,
    payload
  );

  return data;
}